import React, { useState } from "react"
import { Alert, Badge, Button, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { OperationStatus } from "./FeedbackDialogs"
import { FORM_FIELDS, LEGENDS, formCommands, initialForm, playersSee, romanianChanged, variantState } from "./catalogForm"
import { LANGUAGES } from "./catalogView"
import { THEME_ICON_NAMES, ThemeIcon } from "./themeIcons"

const ICON_LABELS = { CourtBooking: "Court booking" }
const Count = ({ value, limit }) => <small className={"feedback-setup__count " + (value.length > limit ? "text-danger font-weight-bold" : "text-muted")}>
  {value.length}/{limit}</small>

// One language's translation of a field: the text players get, its count and its state, with „Correct by hand” and „Use the
// automatic translation”. Nothing to edit for a new item or a changed Romanian text on a server that translates: those are
// translated after saving. On a server that does not (`manualTranslation`), they are written here, with the Romanian.
function VariantRow({ id, field, language, name, limit, item, form, setForm, isNew, changed, manualTranslation }) {
  const variant = item?.variants?.[language]
  const { mode, content } = form.variants[language]
  const set = next => setForm(previous => ({ ...previous, variants: { ...previous.variants, [language]: { ...previous.variants[language], ...next } } }))
  const inputId = `${id}-${language}`
  const row = (input, count, state) => <div className="feedback-setup__lang">
    <Label for={inputId}>{name}</Label>{input}{count || <span />}<span className="feedback-setup__state">{state}</span>
  </div>
  const write = event => {
    const text = event.target.value
    setForm(previous => ({ ...previous, variants: { ...previous.variants,
      [language]: { ...previous.variants[language], content: { ...previous.variants[language].content, [field]: text } } } }))
  }
  if ((isNew || changed) && manualTranslation) {
    const value = content[field] ?? ""
    return row(<Input id={inputId} value={value} invalid={value.length > limit} maxLength={limit} onChange={write} />,
      <Count value={value} limit={limit} />, <Badge className="badge-soft-warning">Write by hand</Badge>)
  }
  if (isNew || changed) return row(<Input id={inputId} value="" readOnly placeholder={isNew ? "Translated automatically after saving" : "Translated again after saving"} />, null,
    changed && <span className="text-muted small">Players keep „{playersSee(variant, field, item.ro[field])}” until then.</span>)
  const state = variantState(variant, mode)
  if (state === "pending") return row(<Input id={inputId} value="" readOnly placeholder="Translating…" />, null,
    <><span className="spinner-border spinner-border-sm text-info" aria-hidden="true" /><span className="small">Translating…</span></>)
  const editable = state === "manual" || state === "required"
  const value = content[field] ?? ""
  const tooLong = value.length > limit
  const onChange = write
  const automatic = () => set({ mode: "automatic", content: Object.fromEntries(Object.keys(content).map(key => [key, variant.automatic[key] ?? ""])) })
  return row(<Input id={inputId} value={value} readOnly={!editable} invalid={tooLong} maxLength={editable ? limit : undefined} onChange={onChange} />,
    <Count value={value} limit={limit} />,
    state === "required" ? <Badge className="badge-soft-danger">Manual translation required</Badge> : <>
      {tooLong && <Badge className="badge-soft-danger">Too long</Badge>}
      <Badge className={editable ? "badge-soft-secondary" : state === "default" ? "badge-soft-info" : "badge-soft-success"}>
        {editable ? "Translated manually" : state === "default" ? "Default" : "Auto-translated"}</Badge>
      {!editable && <Button color="link" size="sm" className="p-0" onClick={() => set({ mode: "manual" })}>Correct by hand</Button>}
      {editable && variant?.automatic && <Button color="link" size="sm" className="p-0" onClick={automatic}>Use the automatic translation</Button>}
    </>)
}

// One Romanian text with its English and Bulgarian rows.
function TranslatedField({ id, field, limit, hint, ...props }) {
  const { form, setForm } = props
  const onChange = event => {
    const text = event.target.value
    setForm(previous => ({ ...previous, ro: { ...previous.ro, [field]: text } }))
  }
  return <fieldset className="feedback-setup__field">
    <legend>{LEGENDS[field]}</legend>
    <div className="feedback-setup__lang">
      <Label for={`${id}-ro`}>Romanian</Label>
      <Input id={`${id}-ro`} value={form.ro[field]} maxLength={limit} onChange={onChange} />
      <Count value={form.ro[field]} limit={limit} />
      <span className="feedback-setup__state"><Badge className="badge-soft-primary">Original</Badge></span>
    </div>
    {hint && <FormText className="feedback-setup__hint">{hint}</FormText>}
    {LANGUAGES.map(([language, name]) => <VariantRow key={language} id={id} field={field} language={language} name={name} limit={limit} {...props} />)}
  </fieldset>
}

// Adds or edits a theme or a reason (docs/design/tournament-feedback, „Formularul”). `target` is `{ kind: 'theme' | 'reason',
// theme, item }`, without `item` when adding; `onSave(commands)` sends the Save's commands (catalogForm.js).
export default function FeedbackForm({ target, catalog, operation, onSave, onClose }) {
  const { kind, theme, item } = target
  const isNew = !item
  const [form, setForm] = useState(() => initialForm(kind, item))
  // This server does not translate (sportya-api `translation.enabled`): a new or changed Romanian text comes with its translations.
  const manualTranslation = catalog.translation?.enabled === false
  const { valid, commands } = formCommands(target, form, catalog.limits, { manualTranslation })
  const changed = romanianChanged(kind, item, form)
  const name = kind === "theme" ? item?.ro.title : item?.ro.text
  const icons = catalog.icons.filter(icon => THEME_ICON_NAMES.includes(icon))
  const shared = { item, form, setForm, isNew, changed, manualTranslation }
  return <Modal isOpen size="lg" centered toggle={onClose} className="feedback-setup-dialog">
    <ModalHeader toggle={onClose}>{isNew ? (kind === "theme" ? "Add theme" : `Add reason · ${theme.ro.title}`) : `Edit ${kind} · ${name}`}</ModalHeader>
    <ModalBody>
      <p className="small text-muted">
        {isNew ? "A code is given when you save and never changes. " : <>Code <code>{item.code}</code> · stays the same for every answer. </>}
        {kind === "theme" && (item?.type === "generalNote" ? "No stars: it uses the general note and appears first, only when that note is 1–3 stars."
          : "Rated with 1–5 stars; its reasons appear at 1–3 stars.")}
      </p>
      {!isNew && <Alert color="warning" className="py-2 small">Change the wording only while the meaning stays the same. For a new meaning, add a
        new {kind} and deactivate this one, so that old and new answers never mix.</Alert>}
      {FORM_FIELDS[kind].map(([field, , limitKey]) => <TranslatedField key={field} id={`${kind}-${field}`} field={field} limit={catalog.limits[limitKey]}
        hint={field === "reasonsLabel" ? "Shown above the reasons, for example „Ce n-a mers?” or „Ce ți-a lipsit?”." : null} {...shared} />)}
      {kind === "theme" && <fieldset className="feedback-setup__field">
        <legend>Icon</legend>
        <div className="feedback-setup__icons" role="radiogroup" aria-label="Icon">
          {icons.map(icon => <label key={icon} className={"feedback-setup__icon-choice" + (form.icon === icon ? " is-selected" : "")}>
            <input type="radio" name="theme-icon" value={icon} checked={form.icon === icon} onChange={() => setForm(previous => ({ ...previous, icon }))} />
            <ThemeIcon name={icon} /><span>{ICON_LABELS[icon] || icon}</span>
          </label>)}
        </div>
        <FormText>From the design system's icons, as players see it next to the title.</FormText>
      </fieldset>}
      <p className="small text-muted mb-0">{(manualTranslation
        ? "Automatic translation is off on this server: write the English and the Bulgarian with the Romanian; Hungarian and Czech show English. "
        : "English and Bulgarian are translated automatically from Romanian; Hungarian and Czech show English. "
          + "A corrected translation is replaced when the Romanian text changes.")
        + (isNew ? ` A new ${kind} starts inactive: check its translations, then activate it.` : "")}</p>
      <OperationStatus operation={operation} form />
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={onClose}>Cancel</Button>
      <Button color="primary" disabled={!valid || operation.busy} onClick={() => onSave(commands)}>
        {operation.busy ? "Saving…" : isNew ? `Add ${kind}` : "Save"}</Button>
    </ModalFooter>
  </Modal>
}
